import React, { useEffect, useMemo, useState } from "react";
import "../styles/shared-ui.css";
import { useAppContext } from "../context/AppContext";
import {
  getKPIById,
  updateKPI,
  getAllUoMs,
  KPI_TYPE_LABELS,
} from "../services/kpiService";
import { toast } from "../utils/toast";
import { resolveRoleName } from "../utils/auth";
import { useLocation, useParams } from "react-router-dom";

const KPIEditPage = () => {
  const { setActiveSection, kpiId: kpiIdFromContext } = useAppContext();
  const { kpiId: kpiIdParam } = useParams();
  const location = useLocation();
  // The URL is the source of truth; context covers in-app navigation.
  // Dashboard is mounted under "/*", so the param usually has to be parsed.
  const routeKpiId = useMemo(() => {
    if (kpiIdParam) return kpiIdParam;
    const m = String(location?.pathname || "").match(/^\/kpis\/([^/]+)\/edit$/i);
    return m?.[1] || null;
  }, [kpiIdParam, location?.pathname]);
  const kpiId = routeKpiId || kpiIdFromContext;
  const [roleChecked, setRoleChecked] = useState(false);
  const [canAccess, setCanAccess] = useState(false);

  const [uoms, setUoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    kpiName: "",
    description: "",
    unitOfMeasure: "",
    kpiType: "TYPE1",
  });

  const setField = (key, val) => setForm((prev) => ({ ...prev, [key]: val }));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let user = null;
        try {
          const raw = sessionStorage.getItem("auth");
          const auth = raw ? JSON.parse(raw) : null;
          user = auth?.user || null;
        } catch {}
        const rn = user ? await resolveRoleName(user) : null;
        const norm = String(rn || "").toUpperCase();
        const allowed = norm === "SUPER_ADMIN" || norm === "SUPERADMIN";
        if (!cancelled) {
          setCanAccess(allowed);
          setRoleChecked(true);
        }
        if (!allowed) setActiveSection("dashboard");
      } catch {
        if (!cancelled) {
          setCanAccess(false);
          setRoleChecked(true);
        }
        setActiveSection("dashboard");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setActiveSection]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        let id = kpiId;
        if (!id) {
          try {
            id = localStorage.getItem("activeKpiId") || null;
          } catch {}
        }
        id = Number(id);
        if (!id || !Number.isFinite(id) || id <= 0) {
          toast.error("No KPI selected");
          setActiveSection("kpi-management");
          return;
        }
        const [kpi, uomData] = await Promise.all([
          getKPIById(id),
          getAllUoMs().catch(() => []),
        ]);
        setUoms(Array.isArray(uomData) ? uomData : []);
        setForm({
          kpiName: kpi.kpiName || "",
          description: kpi.description || "",
          unitOfMeasure: kpi.unitOfMeasure || kpi.kpi_uom?.id || "",
          kpiType: kpi.kpiType || "TYPE1",
        });
      } catch (e) {
        toast.error(e?.message || "Failed to load KPI");
        setActiveSection("kpi-management");
      } finally {
        setLoading(false);
      }
    })();
  }, [kpiId, setActiveSection]);

  const errors = useMemo(() => {
    const e = {};
    if (!String(form.kpiName).trim()) e.kpiName = "KPI Name is required";
    if (!form.unitOfMeasure) e.unitOfMeasure = "Unit of Measure is required";
    return e;
  }, [form]);

  const canSave = useMemo(() => Object.keys(errors).length === 0, [errors]);

  const onSave = async () => {
    if (!canSave || saving) return;
    let id = kpiId;
    if (!id) {
      try {
        id = localStorage.getItem("activeKpiId") || null;
      } catch {}
    }
    id = Number(id);
    try {
      setSaving(true);
      await updateKPI(id, form);
      toast.success("KPI updated successfully");
      setActiveSection("kpi-management");
    } catch (e) {
      toast.error(e?.message || "Failed to update KPI");
    } finally {
      setSaving(false);
    }
  };

  const onCancel = () => setActiveSection("kpi-management");

  if (!roleChecked) {
    return (
      <div className="page-container">
        <div className="page-content">
          <div className="no-results">Loading...</div>
        </div>
      </div>
    );
  }
  if (!canAccess) {
    return (
      <div className="page-container">
        <div className="page-content">
          <div className="no-results">Access denied</div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h2 className="page-title">Edit KPI</h2>
            <p className="page-subtitle">
              Update Key Performance Indicator details
            </p>
          </div>
          <div className="roles-toolbar">
            <button className="secondary-btn" onClick={onCancel}>
              Cancel
            </button>
            <button
              className="primary-btn"
              onClick={onSave}
              disabled={!canSave || saving}
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      </div>

      <div className="page-content">
        {loading ? (
          <div className="no-results">Loading...</div>
        ) : (
          <div className="role-card">
            <div
              className="section-title"
              style={{ marginBottom: 12, fontWeight: 600 }}
            >
              KPI Details
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>KPI Name *</label>
                <input
                  value={form.kpiName}
                  onChange={(e) => setField("kpiName", e.target.value)}
                  placeholder="Enter KPI name"
                />
                {errors.kpiName && (
                  <div
                    className="input-hint"
                    style={{ color: "var(--red-ink-700)", fontSize: 12, marginTop: 4 }}
                  >
                    {errors.kpiName}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>KPI Type *</label>
                <select
                  value={form.kpiType}
                  onChange={(e) => setField("kpiType", e.target.value)}
                >
                  {Object.entries(KPI_TYPE_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Unit of Measure *</label>
                <select
                  value={form.unitOfMeasure}
                  onChange={(e) => setField("unitOfMeasure", e.target.value)}
                >
                  <option value="">Select UoM</option>
                  {uoms.map((uom) => (
                    <option key={uom.id} value={uom.id}>
                      {uom.uomValue}
                      {uom.description ? ` - ${uom.description}` : ""}
                    </option>
                  ))}
                </select>
                {errors.unitOfMeasure && (
                  <div
                    className="input-hint"
                    style={{ color: "var(--red-ink-700)", fontSize: 12, marginTop: 4 }}
                  >
                    {errors.unitOfMeasure}
                  </div>
                )}
              </div>

              <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                <label>Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setField("description", e.target.value)}
                  placeholder="Enter description (optional)"
                  rows={3}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KPIEditPage;
